// src/pages/evcc/EvccTrustedPartners.jsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stack,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";

const pageBg = "#f9fbfe";
const sectionHeaderBg = "#ccd87cff";
const sectionHeaderBorder = "#9fad3d";
const linkBlue = "#7e895d";

function SectionCard({ title, children }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        border: "1px solid #dde4f0",
        mb: 4,
      }}
    >
      <Box
        sx={{
          backgroundColor: sectionHeaderBg,
          borderTop: `4px solid ${sectionHeaderBorder}`,
          px: 3,
          py: 1.5,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1d2736" }}>
          {title}
        </Typography>
      </Box>

      <Box sx={{ p: 3, backgroundColor: "#ffffff" }}>{children}</Box>
    </Paper>
  );
}

function BulletList({ items }) {
  return (
    <List dense sx={{ pl: 0, mt: 0.5 }}>
      {items.map((text) => (
        <ListItem key={text} sx={{ pl: 0 }}>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <ArrowRightIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={text}
            primaryTypographyProps={{ variant: "body2", color: "#111827" }}
          />
        </ListItem>
      ))}
    </List>
  );
}

export default function EvccTrustedPartners() {
  return (
    <Box sx={{ backgroundColor: pageBg, minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Breadcrumb */}
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
          Home &gt; EVCC &gt; Trusted Partners
        </Typography>

        {/* Page title */}
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, color: "#111827" }}
        >
          EVCC – Trusted Partners
        </Typography>

        <Typography variant="body1" sx={{ mb: 4, color: "#4b5563", maxWidth: 900 }}>
          Enhance your EVCC membership by working with trusted partner schemes and
          affiliates that support high consumer protection standards and help you grow
          your business.
        </Typography>

        {/* RECC membership introduction */}
        <SectionCard title="RECC membership – enhance your EVCC membership">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ mb: 2 }}
          >
            {/* RECC logo placeholder */}
            <Box
              sx={{
                width: 160,
                height: 80,
                borderRadius: 2,
                backgroundColor: "#eef2ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#4b5563",
              }}
            >
              RECC logo
            </Box>

            <Typography variant="body2" sx={{ color: "#4b5563" }}>
              If your business also installs small-scale renewable heat or power systems,
              you can expand your EVCC membership by joining the Renewable Energy Consumer
              Code (RECC). As EVCC’s sister code, RECC is designed for businesses working
              in the small-scale renewable energy sector.
            </Typography>
          </Stack>

          <Button
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            component="a"
            href="/assets/evcc/trusted-partners/recc-requirements.pdf" // update if needed
            target="_blank"
            rel="noopener noreferrer"
          >
            Read the full requirements
          </Button>

          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
            EVCC member benefit
          </Typography>
          <BulletList
            items={[
              "Seamless dual membership demonstrating your commitment to consumer protection standards across the industry.",
            ]}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            Key reasons to join
          </Typography>
          <BulletList
            items={[
              "Expand your business to include wider renewable energy installations.",
              "Access free model documents and contracts.",
              "Receive ongoing policy, regulatory and compliance guidance.",
            ]}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            How to apply
          </Typography>
          <Typography variant="body2" sx={{ color: "#4b5563", mb: 1 }}>
            Learn more about RECC and the technologies covered on the RECC website or
            download the application form.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ mb: 2 }}
          >
            <Button
              variant="contained"
              size="small"
              component="a"
              href="/assets/evcc/trusted-partners/recc-application-form.pdf" // update
              target="_blank"
              rel="noopener noreferrer"
            >
              Click to join
            </Button>
            <Button
              variant="outlined"
              size="small"
              component="a"
              href="/assets/evcc/trusted-partners/recc-membership-fees.pdf" // update
              target="_blank"
              rel="noopener noreferrer"
            >
              See membership fees
            </Button>
            <Button
              variant="outlined"
              size="small"
              component="a"
              href="/assets/evcc/trusted-partners/recc-application-guidance.pdf" // update
              target="_blank"
              rel="noopener noreferrer"
            >
              Guidance to apply
            </Button>
          </Stack>

          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
            Contact us
          </Typography>
          <Typography variant="body2" sx={{ color: "#4b5563" }}>
            For more information about joining RECC, please contact:
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: "#4b5563" }}>
            Phone:{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              0207 981 0850
            </Box>
          </Typography>
          <Typography variant="body2" sx={{ color: "#4b5563" }}>
            Email:{" "}
            <Box
              component="a"
              href="mailto:info@recc.org.uk"
              sx={{
                color: linkBlue,
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              info@recc.org.uk
            </Box>
          </Typography>
        </SectionCard>

        {/* TrustMark for EVCC */}
        <SectionCard title="TrustMark for EVCC">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={3}
            sx={{ mb: 2 }}
          >
            {/* TrustMark logo placeholder */}
            <Box
              sx={{
                width: 160,
                height: 80,
                borderRadius: 2,
                backgroundColor: "#eef2ff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 12,
                color: "#4b5563",
              }}
            >
              TrustMark logo
            </Box>

            <Typography variant="body2" sx={{ color: "#4b5563" }}>
              TrustMark is an independent, Government-backed endorsed standards scheme
              developed across the home improvement industry. TrustMark registered
              businesses are listed on TrustMark’s &quot;Find Your Local Tradesmen&quot;
              website where homeowners can search for businesses that install EV
              chargepoints – so your business could be found by more customers!
            </Typography>
          </Stack>

          {/* Video placeholder */}
          <Typography variant="body2" sx={{ mb: 1, color: "#4b5563" }}>
            Watch the introduction video to learn more about TrustMark and how it supports
            EVCC members:
          </Typography>
          <Box
            sx={{
              position: "relative",
              pt: "56.25%", // 16:9
              borderRadius: 2,
              overflow: "hidden",
              mb: 2,
              boxShadow: "0 8px 20px rgba(15, 23, 42, 0.12)",
            }}
          >
            {/* Replace src with the real video URL */}
            <Box
              component="iframe"
              src="https://player.vimeo.com/video/EVCC_TRUSTMARK_VIDEO_ID"
              title="TrustMark introduction video"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                border: 0,
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ mb: 2, color: "#4b5563" }}>
            Adding TrustMark registration to your EVCC membership shows your consumers
            that you not only meet EVCC&apos;s rigorous standards, but also the TrustMark
            Code of Conduct and Customer Charter. This provides reassurance, confidence
            and peace of mind to consumers.
          </Typography>

          <Typography variant="subtitle2" sx={{ mt: 1.5, mb: 0.5 }}>
            EVCC offer
          </Typography>
          <BulletList
            items={["TrustMark registration costs just £50 (+ VAT) per year, in addition to your EVCC membership fee."]}
          />

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>
            How to apply
          </Typography>
          <Typography variant="body2" sx={{ color: "#4b5563", mb: 1 }}>
            You just need to complete the TrustMark application form and return it by
            email.
          </Typography>

          <Button
            variant="contained"
            size="small"
            sx={{ mr: 1.5 }}
            component="a"
            href="/assets/evcc/trusted-partners/trustmark-application-form.pdf" // update
            target="_blank"
            rel="noopener noreferrer"
          >
            Click to join
          </Button>
          <Typography variant="body2" sx={{ mt: 1, color: "#4b5563" }}>
            Send your completed form to{" "}
            <Box
              component="a"
              href="mailto:info@electric-vehicle.org.uk"
              sx={{
                color: linkBlue,
                fontWeight: 600,
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              info@electric-vehicle.org.uk
            </Box>
            .
          </Typography>
        </SectionCard>

        {/* Affiliates */}
        <SectionCard title="Affiliates">
          {/* iChoosr */}
          <Box sx={{ mb: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              sx={{ mb: 1.5 }}
            >
              {/* iChoosr logo placeholder */}
              <Box
                sx={{
                  width: 140,
                  height: 60,
                  borderRadius: 2,
                  backgroundColor: "#eef2ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: "#4b5563",
                }}
              >
                iChoosr logo
              </Box>

              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#111827", mb: 0.5 }}
                >
                  iChoosr
                </Typography>
                <Typography variant="body2" sx={{ color: "#4b5563" }}>
                  iChoosr is a leading platform connecting homeowners with trusted
                  installers of sustainable energy technologies, including electric
                  vehicle (EV) chargers, solar panels, heat pumps, and battery storage.
                  By simplifying the customer journey from interest to installation,
                  iChoosr helps installers grow their business while ensuring
                  high-quality, reliable EV charging solutions reach more households.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* REA – RECHARGE UK */}
          <Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3}
              sx={{ mb: 1.5 }}
            >
              {/* RECHARGE UK logo placeholder */}
              <Box
                sx={{
                  width: 140,
                  height: 60,
                  borderRadius: 2,
                  backgroundColor: "#eef2ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 12,
                  color: "#4b5563",
                }}
              >
                RECHARGE UK logo
              </Box>

              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 600, color: "#111827", mb: 0.5 }}
                >
                  REA – RECHARGE UK
                </Typography>
                <Typography variant="body2" sx={{ color: "#4b5563" }}>
                  RECHARGE UK is the UK’s largest dedicated forum for the electric
                  vehicle (EV) charging sector. With a membership of around 100
                  organisations spanning charge-point operators, manufacturers, green
                  finance and legal firms, and other innovators, the forum brings
                  together leading voices working to advance the EV infrastructure,
                  consumer experience, and policy environment.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: "#4b5563" }}>
                  RECHARGE UK is convened by REA (Renewable Energy Association), the
                  UK’s not-for-profit trade association for the renewable energy and
                  clean technology sector.
                </Typography>
              </Box>
            </Stack>
          </Box>
        </SectionCard>
      </Container>
    </Box>
  );
}
